import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import StatusSelect from "@/components/StatusSelect";
import {
    MYTASK_STATUS_STYLES,
    MYTASK_STATUS_DOT,
    myTaskStatusOptions,
} from "@/components/StatusBadge";
import MyTaskFormDialog from "@/components/MyTaskFormDialog";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_BADGE = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-700 border-slate-200",
};

const SECTIONS = [
    { category: "professional", title: "Professional" },
    { category: "combined_billing", title: "Combined Billing" },
    { category: "personal", title: "Personal" },
];

function fmtDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return d;
    }
}

// Inline, auto-saving remarks editor. Saves on blur when changed; while it's
// focused the live-refresh layer is paused, so typing is never interrupted.
function InlineRemarks({ task, onSave }) {
    const [val, setVal] = useState(task.remarks || "");
    useEffect(() => {
        setVal(task.remarks || "");
    }, [task.remarks]);
    const commit = () => {
        if ((val || "") !== (task.remarks || "")) onSave(val.trim() ? val.trim() : null);
    };
    return (
        <Textarea
            rows={2}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onClick={(e) => e.stopPropagation()}
            placeholder="Add remarks…"
            className="min-h-[42px] w-full resize-y text-sm"
            data-testid={`mytask-remarks-${task.id}`}
        />
    );
}

function Section({ category, title, tasks, onAdd, onEdit, onStatusChange, onPatch }) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [showCompleted, setShowCompleted] = useState(false);

    const showClient = category !== "personal";
    const statusOptions = myTaskStatusOptions(category);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return tasks.filter((t) => {
            if (statusFilter !== "all" && t.status !== statusFilter) return false;
            if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
            if (q) {
                const hay = `${t.title || ""} ${t.work_type || ""} ${t.client_name || ""} ${t.remarks || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [tasks, search, statusFilter, priorityFilter]);

    const colCount = showClient ? 8 : 7;

    // "Completed" reads as "Billed" in Professional / Combined Billing.
    const completedLabel =
        statusOptions.find((o) => o.value === "completed")?.label || "Completed";
    const activeRows = filtered.filter((t) => t.status !== "completed");
    const completedRows = filtered.filter((t) => t.status === "completed");
    const expandCompleted = showCompleted || statusFilter === "completed";

    const renderRow = (t) => (
        <TableRow
            key={t.id}
            className="sdc-table-row cursor-pointer"
            data-testid={`mytask-row-${t.id}`}
            onClick={() => onEdit(t)}
        >
            <TableCell className="font-semibold text-foreground">{t.title}</TableCell>
            <TableCell>{t.work_type}</TableCell>
            {showClient && (
                <TableCell className="text-sm">
                    {t.client_name || <span className="text-muted-foreground">—</span>}
                </TableCell>
            )}
            <TableCell className="text-sm">{fmtDate(t.task_date)}</TableCell>
            <TableCell className="text-sm">{fmtDate(t.deadline_date)}</TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
                <StatusSelect
                    value={t.status}
                    onChange={(v) => onStatusChange(t.id, v)}
                    options={myTaskStatusOptions(t.category)}
                    styles={MYTASK_STATUS_STYLES}
                    dot={MYTASK_STATUS_DOT}
                />
            </TableCell>
            <TableCell>
                <span
                    className={`sdc-pill border ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium}`}
                >
                    {t.priority?.toUpperCase()}
                </span>
            </TableCell>
            <TableCell
                className="min-w-[360px] align-top"
                onClick={(e) => e.stopPropagation()}
            >
                <InlineRemarks task={t} onSave={(v) => onPatch(t.id, { remarks: v })} />
            </TableCell>
        </TableRow>
    );

    return (
        <Card className="sdc-card overflow-hidden" data-testid={`mytask-section-${category}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        Section
                    </div>
                    <h2 className="font-display text-xl font-extrabold tracking-tight text-foreground">
                        {title}
                    </h2>
                </div>
                <Button size="sm" onClick={() => onAdd(category)} data-testid={`mytask-add-${category}`}>
                    <Plus className="mr-1.5 h-4 w-4" /> Add task
                </Button>
            </div>

            {/* Per-section filters */}
            <div className="grid grid-cols-1 gap-3 border-b border-border bg-secondary/20 px-5 py-3 md:grid-cols-12">
                <div className="md:col-span-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search task, work, client, remarks..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid={`mytask-search-${category}`}
                        />
                    </div>
                </div>
                <div className="md:col-span-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid={`mytask-filter-status-${category}`}>
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {statusOptions.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="md:col-span-3">
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger data-testid={`mytask-filter-priority-${category}`}>
                            <SelectValue placeholder="Priority" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All priorities</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                            <TableHead className="min-w-[180px]">Task</TableHead>
                            <TableHead>Type of Work</TableHead>
                            {showClient && <TableHead>Client</TableHead>}
                            <TableHead>Start Date</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Priority</TableHead>
                            <TableHead className="min-w-[360px] w-[28%]">Remarks</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                                    No tasks in this section.
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {activeRows.map(renderRow)}
                                {completedRows.length > 0 && (
                                    <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                                        <TableCell colSpan={colCount} className="py-0">
                                            <button
                                                type="button"
                                                onClick={() => setShowCompleted((s) => !s)}
                                                className="flex w-full items-center gap-2 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                                                data-testid={`mytask-toggle-completed-${category}`}
                                            >
                                                {expandCompleted ? (
                                                    <ChevronUp className="h-4 w-4" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4" />
                                                )}
                                                {completedLabel} ({completedRows.length})
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {expandCompleted && completedRows.map(renderRow)}
                            </>
                        )}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
}

export default function MyTasks() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [addCategory, setAddCategory] = useState("professional");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/my-tasks");
            setTasks(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useLiveRefresh(load);

    const byCategory = (category) => tasks.filter((t) => t.category === category);

    const onAdd = (category) => {
        setEditing(null);
        setAddCategory(category);
        setShowForm(true);
    };
    const onEdit = (t) => {
        setEditing(t);
        setShowForm(true);
    };
    const onPatch = async (id, fields) => {
        try {
            await api.patch(`/my-tasks/${id}`, fields);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const onStatusChange = (id, status) => onPatch(id, { status });

    return (
        <div className="space-y-6" data-testid="my-tasks-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Admin
                </div>
                <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                    My Tasks
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Your private board. Completed engagements from the Task Manager appear here
                    as billing follow-ups.
                </p>
            </div>

            {loading ? (
                <div className="grid place-items-center py-32">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            ) : (
                <div className="space-y-6">
                    {SECTIONS.map((s) => (
                        <Section
                            key={s.category}
                            category={s.category}
                            title={s.title}
                            tasks={byCategory(s.category)}
                            onAdd={onAdd}
                            onEdit={onEdit}
                            onStatusChange={onStatusChange}
                            onPatch={onPatch}
                        />
                    ))}
                </div>
            )}

            <MyTaskFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                task={editing}
                defaultCategory={addCategory}
                onSaved={load}
            />
        </div>
    );
}
